import { Button, Spinner } from "@heroui/react";
import * as dn from "dnum";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { usePrivateBalance, useTokenBalance } from "../queries/useTokenBalances";

/**
 * Simple component to display public balance.
 * Only reads from the batch cache - NEVER makes individual queries.
 */
export function SimplePublicBalance({ tokenAddress, decimals }: { tokenAddress: string; decimals: number }) {
  const { publicBalance, isLoading } = useTokenBalance(tokenAddress);

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  if (publicBalance === undefined) {
    return <span>0</span>;
  }

  return <span>{dn.format([publicBalance, decimals], { digits: 4, decimalsRounding: "ROUND_DOWN" })}</span>;
}

/**
 * Component to display private balance with reveal button.
 * Private balances are fetched on-demand (slow) so they're hidden by default.
 */
export function SimplePrivateBalance({
  tokenAddress,
  decimals,
  autoFetch = false,
  onRevealedChange,
}: {
  tokenAddress: string;
  decimals: number;
  autoFetch?: boolean;
  onRevealedChange?: (revealed: boolean) => void;
}) {
  const [revealed, setRevealed] = useState(autoFetch);
  const { privateBalance, isLoading } = usePrivateBalance(tokenAddress, revealed);

  const handleReveal = () => {
    setRevealed(true);
    onRevealedChange?.(true);
  };

  const handleHide = () => {
    setRevealed(false);
    onRevealedChange?.(false);
  };

  if (!revealed) {
    return (
      <Button
        size="sm"
        variant="light"
        onPress={handleReveal}
        startContent={<EyeIcon size={14} />}
        className="h-6 min-w-0 px-2 text-xs text-[#91A7B6]"
      >
        Reveal
      </Button>
    );
  }

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span>{dn.format([privateBalance ?? 0n, decimals], { digits: 4, decimalsRounding: "ROUND_DOWN" })}</span>
      <Button size="sm" variant="light" isIconOnly onPress={handleHide} className="h-4 w-4 min-w-0 text-[#91A7B6]">
        <EyeOffIcon size={12} />
      </Button>
    </div>
  );
}

/**
 * Display component that shows a balance value directly (no fetching).
 */
export function BalanceDisplay({ balance, decimals }: { balance: bigint; decimals: number }) {
  return <span>{dn.format([balance, decimals], { digits: 4, decimalsRounding: "ROUND_DOWN" })}</span>;
}
